// Which OAP measures CMS REQUIRES for a given beneficiary + report, re-exported for the
// reporting modal. The matrix itself is the SHARED module WF37 inlines
// (scripts/cms-oap-measures.mjs) — sanctioned cross-root import, same arrangement as the
// subtrack resolver, so the modal and the workflow can never disagree about what is required.
// Parity is enforced by static S-105; the app never imports from '../scripts' directly.
export {
  classifyDiagnoses,
  requiredMeasures,
  evaluateRequiredMeasures,
  windowDaysFor,
  overrideAccepted,
  OVERRIDE_MIN_REASON,
} from '../../../scripts/cms-oap-measures.mjs';

export type {
  DiagnosisClasses,
  OapReportKind,
  RequiredMeasuresSpec,
  RequiredMeasuresVerdict,
} from '../../../scripts/cms-oap-measures.mjs';

/**
 * Days until the CMS baseline deadline (60 days from alignment). Negative = past it.
 * The override screen shows this because "file incomplete or miss the window" is only a real
 * choice if the operator can see which side of the cliff they are on.
 */
export function daysToBaselineDeadline(
  cmsAlignmentDate: string | null | undefined,
  now: number = Date.now(),
): number | null {
  const t = Date.parse(String(cmsAlignmentDate ?? ''));
  if (!Number.isFinite(t)) return null;
  return Math.ceil((t + 60 * 86400000 - now) / 86400000);
}

export interface RequiredMissingEntry { slug: string; why?: string }

export interface RequirementBanner {
  tone: 'bad' | 'moderate';
  headline: string;
  explainer: string;
}

/**
 * The verdict banner, recomputed against what is CURRENTLY in the form.
 *
 * WF37's verdict is about what was SENT — nothing — so it names every required measure as missing
 * and says "enter the values below". Once those values are sitting in the form (typed, or prefilled
 * from the Elation chart) that instruction is already carried out, and the screen ends up telling
 * the operator to do something they have just done while a callout underneath says the opposite.
 * A banner that argues with the form is worse than no banner: the operator has to work out which
 * half to believe, on a Medicare filing.
 *
 * `filled` is the set of slugs with a VALID value in the form (the validated payload, not the raw
 * drafts) — a value the guard would reject is not an answer.
 */
export function requirementBannerCopy({
  requiredMissing, filled, labelOf, reason, fromChart = 0,
}: {
  requiredMissing: RequiredMissingEntry[];
  filled: Set<string>;
  labelOf: (slug: string) => string;
  reason?: string | null;
  fromChart?: number;
}): RequirementBanner | null {
  const missing = (requiredMissing || []).map((m) => String(m?.slug ?? '')).filter(Boolean);
  if (missing.length === 0) return null;
  const still = missing.filter((s) => !filled.has(s));
  const now = missing.filter((s) => filled.has(s));
  // Nothing has changed since the verdict — WF37's own copy is the most accurate thing available.
  if (now.length === 0) return null;

  const list = (slugs: string[]): string => slugs.map(labelOf).join(', ');
  const verb = (slugs: string[]): string => (slugs.length === 1 ? 'is' : 'are');
  const chart = fromChart > 0
    ? ` (${fromChart} read from the Elation chart — check ${fromChart === 1 ? 'it' : 'them'} against the report)`
    : '';
  // "Nothing has been sent" leads every branch: it is the one fact the operator must not lose.
  if (still.length === 0) {
    return {
      tone: 'moderate',
      // Leads with the fact, like every explainer and like WF37's own display: the operator's
      // first read must not be that something happened at CMS. It also avoids "entered" —
      // these values are often PREFILLED from the Elation chart, so nobody entered them.
      headline: 'Nothing sent yet — all required values are filled in',
      explainer: `Nothing has been sent to CMS yet. ${list(now)} ${verb(now)} filled in below${chart}. Re-check the bundle to see exactly what would be sent, then confirm.`,
    };
  }
  return {
    tone: 'bad',
    headline: `${still.length} required measure${still.length === 1 ? '' : 's'} still missing: ${list(still)}`,
    explainer: `Nothing has been sent to CMS yet. ${list(now)} ${verb(now)} filled in below${chart}. Still needed: ${list(still)} — enter ${still.length === 1 ? 'it' : 'them'} below, or override with a written reason. ${reason && /60-day/.test(reason) ? 'Filing incomplete keeps the beneficiary aligned; missing the 60-day window unaligns them.' : ''}`.trim(),
  };
}
